import { motion } from "framer-motion";
import { scrollToSection } from "@/lib/lenis";

const BOARDROOM_IMAGE =
  "https://images.unsplash.com/photo-1786525234451-c97bd5ccfa48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxuZXclMjB6ZWFsYW5kJTIwcm9sbGluZyUyMGdyZWVuJTIwaGlsbHMlMjBmYXJtbGFuZCUyMHdhaWthdG8lMjBsYW5kc2NhcGUlMjBjb3VudHJ5c2lkZXxlbnwwfHx8fDE3OTEwNjk4Mjl8MA&ixlib=rb-4.1.0&q=85";

const AERIAL_IMAGE =
  "https://images.unsplash.com/photo-1488354028423-039ac1e23357?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBsYW5kJTIwZGV2ZWxvcG1lbnQlMjBzdWJkaXZpc2lvbiUyMGZpZWxkJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDk0ODcxMXww&ixlib=rb-4.1.0&q=85";

const STRENGTHS = [
  {
    title: "Capital Preservation",
    text: "Protecting capital is central to our investment approach. We carefully assess each opportunity and structure our investments to manage risk while supporting growth. By investing our own capital, we take a direct and lasting interest in the success of the businesses we back.",
    testid: "pillar-capital-preservation",
  },
  {
    title: "Hands-On Partnership",
    text: "We work closely with management teams, contributing practical experience and operational support. Our investments across complementary sectors create opportunities to share knowledge and resources, from logistics and distribution to construction and land development.",
    testid: "pillar-partnership",
  },
  {
    title: "Generational Vision",
    text: "Our investment decisions are guided by long-term returns. We aim to build enduring, sustainable businesses that create value and contribute to the economy for generations to come.",
    testid: "pillar-generational",
  },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="overflow-hidden rounded-lg shadow-[0_16px_50px_rgba(18,52,78,0.14)]"
          data-testid="about-image-frame"
        >
          <img
            src={BOARDROOM_IMAGE}
            alt="Rolling green Waikato farmland, New Zealand"
            className="w-full h-[340px] lg:h-[420px] object-cover"
          />
        </motion.div>
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#12344E] leading-tight">
            A privately owned investment company
          </h2>
          <p className="mt-6 text-base text-[#4A6076] leading-relaxed">
            Jamsac Investments is a privately owned investment company
            headquartered in New Zealand, with an office in Melbourne,
            Australia.
          </p>
          <p className="mt-4 text-base text-[#4A6076] leading-relaxed">
            We build and manage a diversified portfolio of property, businesses
            and strategic investments across New Zealand and Australia. Our
            approach is practical and long term: we look for well-run
            opportunities backed by strong fundamentals, capable people and
            clear potential for sustainable growth.
          </p>
          <button
            data-testid="about-cta-approach"
            onClick={() => scrollToSection("#approach")}
            className="mt-8 px-8 py-3.5 rounded-full bg-[#12344E] text-white text-xs font-bold uppercase tracking-[0.12em] hover:bg-[#1F6191] transition-colors duration-300"
          >
            Our Investment Approach
          </button>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-2 rounded-lg overflow-hidden shadow-[0_16px_50px_rgba(18,52,78,0.14)]">
        <div className="bg-[#1F6191] px-8 lg:px-12 py-14 lg:py-20">
          <motion.div
            variants={fade}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Key strengths of Jamsac Investments
            </h2>
            <p className="text-white/85 leading-relaxed mt-5 max-w-lg">
              We invest in quality businesses with a focus on long-term
              ownership and sustainable growth by adding value through
              industry expertise and operational involvement.
            </p>
          </motion.div>
          <div className="mt-10 space-y-9">
            {STRENGTHS.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fade}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1 }}
                data-testid={s.testid}
              >
                <h3 className="text-lg font-bold text-white">{s.title}</h3>
                <p className="text-sm text-white/80 leading-relaxed mt-2 max-w-lg">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <div className="min-h-[360px] lg:min-h-0">
          <img
            src={AERIAL_IMAGE}
            alt="Aerial view of New Zealand land development"
            className="w-full h-full object-cover"
          />
        </div>
        </div>
      </div>
    </section>
  );
}
